'use strict';

namespace Core.Booking {

    type SearchUnitService = Search.SearchUnitService;
    export interface BookingService {
        driverOptions: {name:() => string, value: DriverType}[];
        trimBooking(booking : Booking ) : TrimmedBooking;
        prepareBooking(basketId: string,
                                bookingId: string,
                                tripId: string,
                                userData: UserData ) : ng.IPromise<Booking>;
        getBooking(bookingId: string, tripId?: string) : ng.IPromise<Booking>;
        updateBooking(b : Booking, description? : string) : ng.IPromise<any>;
        book(booking: Booking, tripId: string, basketId: string) : ng.IPromise<BookingResponse>;
        getCpgStatus(id: number, $scope: any) : null | ng.IPromise<{
            paRes: string;
            prePaymentId: string;
            error: string;
        }>;
        getUrl() : string;
        getAdultTitles() : () => string;
        copyFromContact(booking: Booking, e: Event) : void;
        detailsMatch(booking: Booking) : boolean;
        clearContact(userData : UserData) : void;
        clearGuests(rooms: Room[]) : void;
        checkValidEmail(email: string) : ng.IPromise<any>;
        updateDeposit(deposit: ng.INgModelController,
                               payment : PaymentDetails,
                               totalCost,
                               minDeposit);
        paypalPostBooking(paypalResult : PayPalResponse) : void;
        getDiscountForCard(bookingId: string,
                            cardNumber: string,
                            domainId: number,
                            cultureCode: string) : ng.IPromise<any>;
        getCardDiscountsForDomain(domainId: number, cultureCode: string) : ng.IPromise<any>;
        setTrackingTag(tag : string) : void;
        getTrackingTag(cb) : () => any;
        pauseRecording(agentIpAddress: string) : void;
        resumeRecording(agentIpAddress: string) : void;
        browserDetails(browserDetails: any) : void;
    }

    interface IWindow extends ng.IWindowService {
        paypal: { checkout : { startFlow: (s) => void}};
    }

    export interface PrepareBookingRequest {
        BookingProcessId: string;
        BasketId: string;
        TripId: string;
        CultureCode: string;
        IsRebook: boolean;
    }

    export class BookingRequest {
        constructor(public TripId:string,
            public BookingProcessId: string,
            public UserData: UserData,
            public CultureCode: string,
            public DomainId: number,
            public ApplicationType: number,
            public LinkedBookingId: number | undefined,
            public TrackingTag: string | null,
            public CheckLivePrices: boolean,
            public BasketId: string){
        }
    }

    angular
        .module('core')
        .service('bookingService', bookingService);

    function bookingService($http : Core.IHttpService,
                            resourceService : Core.ResourceService,
                            basketStore,
                            searchUnitService : SearchUnitService,
                            $q: ng.IQService,
                            $location: ng.ILocationService,
                            $timeout: ng.ITimeoutService,
                            $interval: any,
                            preferences,
                            loggingService,
                            urlUtils,
                            $routeParams: any,
                            localStorageProvider: any,
                            $window: IWindow,
                            mandatoryTransferService,
                            authService: any,
                            $rootScope : ng.IRootScopeService) : BookingService {
        const url: string = '/api2/bookings/';
        const gmtUrl: string = '/api2/callmgmt/';
        const driverOptions = [
            {name: resourceService.getDeferred('NOTADRIVER'), value: DriverType.NotADriver},
            {name: resourceService.getDeferred('MAINDRIVER'), value: DriverType.MainDriver},
            {name: resourceService.getDeferred('ADDITIONALDRIVER'), value: DriverType.AdditionalDriver}
        ];
        let promise: ng.IPromise<Booking> | null;

        const service: BookingService = {
            driverOptions,
            trimBooking,
            prepareBooking,
            getBooking,
            updateBooking,
            book,
            getCpgStatus,
            getUrl,
            getAdultTitles,
            copyFromContact,
            detailsMatch,
            clearContact,
            clearGuests,
            checkValidEmail,
            updateDeposit,
            paypalPostBooking,
            getDiscountForCard,
            getCardDiscountsForDomain,
            setTrackingTag,
            getTrackingTag,
            pauseRecording,
            resumeRecording,
            browserDetails
        };

        function setTrackingTag(tag: string): ng.IPromise<any> {
            return localStorageProvider.setItem('trackingTag', tag);
        }

        function getTrackingTag(cb): () => string | null | Error {
            return localStorageProvider.getItem('trackingTag', (error, value) => cb(value));
        }

        function removeTrackingTag(): ng.IPromise<any> {
            return localStorageProvider.removeItem('trackingTag');
        }

        function _enhanceBooking(booking: Booking): ng.IPromise<Booking> {
            //this has to be done on the client because PayPalEnabled is dictated by whether or not cookies are enabled.
            booking.PaymentOptions = _.filter(
                booking.PaymentOptions,
                option => TRPCONFIG.PayPalEnabled || !option.IsPayPalPayment
            );

            return $q.when(booking).then(setDashboardPreferences);
        }

        function setDashboardPreferences(booking: Booking): ng.IPromise<Booking> {
            if (!TRPCONFIG.Domain.DashboardSettingsEnabled) {
                return $q.when(booking);
            }
            return preferences.get('smokingRoom').then(smokingRoom => {
                return {
                    ...booking,
                    UserData: {
                        ...booking.UserData,
                        Rooms: booking.UserData.Rooms.map(room => {
                            const [ yes, no ] = [ 1, 2 ];

                            let hasSpecialRequest = room.SpecialRequest === '' || room.SpecialRequest == null;
                            let isSmokingRoomSet = smokingRoom === yes || smokingRoom === no;

                            if (hasSpecialRequest && isSmokingRoomSet) {
                                let smokingRequestResource = smokingRoom === yes
                                    ? 'SMOKINGROOMREQUEST'
                                    : 'NOSMOKINGROOMREQUEST';
                                room.SpecialRequest = resourceService.getDeferred(smokingRequestResource)();
                            }

                            return room;
                        })
                    }
                };
            });

        }

        function paypalPostBooking(paypalResult : PayPalResponse) : void {
            if (TRPCONFIG.Domain.PaypalInContextEnabled) {
                const token = urlUtils.parse(paypalResult.AcsUrl).qs.token;
                $window.paypal.checkout.startFlow(token);
            } else {
                $window.location.href = paypalResult.AcsUrl;
            }
        }

        function trimBooking(booking : Booking ) : TrimmedBooking {
            let copy = _.cloneDeep(booking);
            return {
                UserData: copy.UserData,
                BookingProcessId: copy.BookingProcessId,
                LinkedBookingId: copy.LinkedBookingId
            };
        }

        function prepareBooking(basketId: string,
                                bookingId: string,
                                tripId: string,
                                userData: UserData) : ng.IPromise<Booking> {
            const req : PrepareBookingRequest = {
                BookingProcessId: bookingId,
                BasketId: basketId,
                TripId: tripId,
                IsRebook: !!$routeParams.BookingRef,
                CultureCode: TRPCONFIG.Domain.CultureCode
            };

            promise = $http.post<Booking>('website/booking/preparebooking', req)
                .then(({ data: booking }) => {
                    return basketStore.applyCancelations(basketId, booking.BookingProcessId)
                        .then(() => booking);
                })
                .then((booking: Booking) => {
                    if (userData) {
                        booking.UserData = userData;
                    }

                    return $q.all([
                        basketStore.rules(basketId),
                        basketStore.carHire(basketId),
                        _enhanceBooking(_.cloneDeep(booking))
                    ]);
                })
                .then(([rules, carHire, booking]) => {
                    if (rules.captureFlight) {
                        const mode = searchUnitService.transfer;
                        booking.UserData.ConnectingFlight = {
                            OutboundDate: searchUnitService.startDate(mode),
                            InboundDate: searchUnitService.endDate(mode)
                        };
                    }
                    if (TRPCONFIG.Domain.MandatoryExtrasEnabled) {
                        let connectingFlight = booking.UserData.ConnectingFlight;
                        let flightWithDetaild = mandatoryTransferService.addFlightDetails(connectingFlight);
                        booking.UserData.ConnectingFlight = flightWithDetaild;
                    }
                    if (carHire) {
                        booking.BookingRules.AdditionalDrivers = carHire.AdditionalDrivers;
                    }
                    if (rules.captureMainDriver) {
                        let main = false;
                        let additional = 0;
                        let { AdditionalDrives: additionalDrivers } = booking.BookingRules;
                        booking.UserData.Rooms = booking.UserData.Rooms.map(room => ({
                            ...room,
                            Guests: room.Guests.map(guest => {
                                if (!main && guest.DriverType !== 1) {
                                    main = true;
                                    guest.DriverType = 1;
                                } else if (additional < additionalDrivers && guest.Type === 1) {
                                    guest.DriverType = 2;
                                    additional++;
                                }
                                return guest;
                            }),
                        }));
                    }

                    service.updateBooking(booking);
                    return booking;
                });
            return <ng.IPromise<Booking>>promise;
        }

        function checkValidEmail(email: string) : ng.IPromise<any> {
            const domain = TRPCONFIG.Domain;
            return $http.post<boolean>(`${url}validateemail`, {
                DomainId: domain.DomainId,
                CultureCode: domain.CultureCode,
                Email: email
            }).then(result => {
                if (result && result.data) {
                    return $q.resolve();
                }
                return $q.reject();
            });
        }

        function getBooking(bookingId: string, tripId?: string) : ng.IPromise<Booking> {
            if (promise != null && !TRPCONFIG.Domain.isAgentMode) {
                return promise.then(_enhanceBooking);
            }

            promise = $http.get<Booking>(
                `website/booking/getbooking?${$.param({
                    TripId: tripId,
                    BookingProcessId: bookingId,
                    CultureCode: TRPCONFIG.Domain.CultureCode
                })}`, {
                customErrCallback(data) {
                    if (data.status === 404) {
                        loggingService.logError(`Booking Already Done: booking.service: getbooking?${$.param({
                            TripId: tripId, BookingProcessId: bookingId,
                            CultureCode: TRPCONFIG.Domain.CultureCode
                        })}, DomainId:${TRPCONFIG.Domain.DomainId}, href:"${$location.absUrl()}`);
                        $location.path('bookingalreadydone');
                    } else {
                        return true;
                    }
                }
            }).then(function (result) {
                if (result) {
                    return _enhanceBooking(_.cloneDeep(result.data));
                }
                promise = null;
            });

            return <ng.IPromise<Booking>>promise;
        }

        function updateBooking(booking : Booking, description? : string) : ng.IPromise<any> {
            const copy = _.cloneDeep<Booking>(booking);
            //make sure that we are not sending card data when calling update booking
            _.get(copy, 'UserData.PaymentDetails.CardDetails') && delete copy.UserData.PaymentDetails.CardDetails;
            promise = $q.when(booking);
            return $http.post(`${url}updatebooking`, {
                DomainId: TRPCONFIG.Domain.DomainId,
                CultureCode: TRPCONFIG.Domain.CultureCode,
                BookingProcessId: booking.BookingProcessId,
                UserData: copy.UserData,
                Description: description
            }, {ignoreErrors: true}).then(() => promise != null ? promise : booking).then(() => {
                const cookieName = authService.createCookieName();
                if(cookieName) {
                    authService.doesHttpOnlyCookieExist(cookieName);
                }
            });
        }

        function browserDetails() {
            const browserDetails = {
                ScreenHeight: window.screen.height,
                ScreenWidth: window.screen.width,
                ColorDepth: window.screen.colorDepth,
                AdditionalDetails: {
                    ExtraData: [
                        {
                            Name: null,
                            value: null
                        }
                    ]
                }
            };

            return browserDetails;
        }

        function book(booking: Booking, tripId: string, basketId: string) : ng.IPromise<BookingResponse> {
            const domain = TRPCONFIG.Domain;
            //enable timeouts only for desktop
            const isMobile = TRPCONFIG.IsMobileView;
            const checkLivePrice = domain.CheckLivePrices && !isMobile;
            let skywardsUser = JSON.parse(sessionStorage.getItem('skywardsLoginUser') || '{}');
            let skywardsLoyalityNumber = sessionStorage.getItem('SkywardsLoyalityNumber');

            return $q((res, rej) => {
                service.getTrackingTag(value => {
                    if (skywardsUser && skywardsUser.data && skywardsUser.data.Contact) {
                        booking.UserData.Contact.LoyaltyNumber = skywardsUser.data.Contact.LoyaltyId;
                    } else if (skywardsLoyalityNumber) {
                        booking.UserData.Contact.LoyaltyNumber = skywardsLoyalityNumber;
                    }

                    booking.UserData['BrowserDetails'] = browserDetails();

                    const req = new BookingRequest(tripId,
                        booking.BookingProcessId,
                        booking.UserData,
                        domain.CultureCode,
                        domain.DomainId,
                        TRPCONFIG.ApplicationType,
                        booking.LinkedBookingId,
                        value,
                        checkLivePrice,
                        basketId);

                    // CPG iframe domains normalise online card payments to the hosted card type.
                    // External FOP flows such as Town Office Collection provide their own payment
                    // option and must not be overwritten here.
                    if (TRPCONFIG.Domain2.CPGIframeUpdate && !req.UserData.PaymentDetails.IsExternalFop) {
                        let cd = req.UserData.PaymentDetails.CardDetails;
                        cd.CardType = 370;
                        cd.PaymentTypeId = 36;
                    }

                    $http.post<BookingResponse>(`${url}createbooking`, req).then(result => {
                        sessionStorage.setItem('PaymentPopup', 'true');
                        // Discard booking after creation if there are no messages and the status is complete
                        if (result.data.Messages && result.data.Messages.length === 0 &&
                            (result.data.Status === CreateBookingStatus.Complete ||
                            result.data.Status === CreateBookingStatus.HostedPayment)) {
                            sessionStorage.removeItem('PaymentPopup');
                            promise = null;
                            removeTrackingTag();
                        }
                        res(result.data);
                    }).catch(error => {
                        res(error.data);
                    });
                });
            });

        }

        function getCpgStatus(prePaymentId): null | ng.IPromise<{
            paRes: string;
            prePaymentId: string;
            error: string;
        }> {
            const pollDelayMs = TRPCONFIG.Domain.CpgStatusIntervalDelay;
            const stopApiMs = TRPCONFIG.Domain.CpgStatusTimeout;
            if (!prePaymentId) {
                return null;
            }
            const host = $location.host();
            const protocol = $location.protocol();
            const cpgstatusURL = `${protocol}://${host}/cpgstatus?prebookpaymentid=${prePaymentId}`;
            return $q((res, rej) => {
                let interval = $interval(() => {
                    $http.get(`${cpgstatusURL}`).then((result) => {
                        const bookingPath = $location.path();

                        if (result.data.Status === 'SUCCESS') {
                            const cpgStatus = result.data.Status;
                            const authCode =
                                result.data && result.data.AuthCode;
                            const paRes =
                                cpgStatus === 'SUCCESS'
                                    ? `^${authCode}^^^AUTHORISED`
                                    : '';

                            const response = {
                                paRes,
                                prePaymentId,
                                error: '',
                            };
                            res(response);
                            $interval.cancel(interval);
                        }

                        if (bookingPath && !bookingPath.includes('book')) {
                            console.log('cpgStatus cancelled');
                            const response = {
                                paRes: '',
                                prePaymentId: '',
                                error: '',
                            };
                            res(response);
                            $interval.cancel(interval);
                        }

                        if (result && result.status !== 200) {
                            console.log('cpgStatus result.status', result.status);
                            const response = {
                                paRes: '',
                                prePaymentId: '',
                                error: '',
                            };
                            res(response);
                            $interval.cancel(interval);
                        }

                        if(result.data.Status === 'FAILED') {
                            // payment failed so stop polling
                            // Log an error?
                            const error = 'FAILED';
                            const response = {
                                paRes: '',
                                prePaymentId: '',
                                error,
                            };
                            res(response);
                            $interval.cancel(interval);
                        }

                        if(result.data.Status === 'STOP') {
                            // stop polling
                            // Duplicate, log error
                            console.log('stop polling');
                            const response = {
                                paRes: '',
                                prePaymentId: '',
                                error: '',
                            };
                            res(response);
                            $interval.cancel(interval);
                        }

                        if(TRPCONFIG.Domain2.PrePaymentID && TRPCONFIG.Domain2.PrePaymentID !== prePaymentId) {
                            // stop polling
                            // PrePaymentID updated
                            console.log('stop polling - payment updated');
                            const response = {
                                paRes: '',
                                prePaymentId: '',
                                error: '',
                            };
                            res(response);
                            $interval.cancel(interval);
                        }
                    });
                }, pollDelayMs);

                $timeout(() => {
                    $interval.cancel(interval);
                    const error = 'TIMER';
                    const response = {
                        paRes: '',
                        prePaymentId: '',
                        error,
                    };
                    $rootScope.$broadcast('cpgStatusAPIError', response);
                }, stopApiMs);
            });
        }

        function getUrl() : string {
            return url;
        }

        function _getTitleIndex(t: string) : number {
            if (!t) {
                return -1;
            }

            const clean = s =>
                s.toLowerCase()
                    .replace(/[.-/#!$%^&*;:{}=\-_`~()]/g, '')
                    .replace(/\s{2,}/g, ' ');

            const titlesString = clean(getAdultTitles()());
            const titleArray = titlesString.split(',');

            return titleArray
                .indexOf(clean(t));
        }

        function copyFromContact(booking: Booking, e: Event) : void {
            const guest = _.get<Person>(booking, 'UserData.Rooms[0].Guests[0]');
            const contact = booking.UserData.Contact;
            const titles = getAdultTitles()();

            guest.FirstName = contact.FirstName;
            guest.LastName = contact.LastName;

            const index = _getTitleIndex(contact.Title);

            if (index !== -1) {
                guest.Title = titles.split(',')[index];
            }

            if (!!guest.FirstName && !!guest.LastName && index !== -1) {
                service.updateBooking(booking);
                if (e) {
                    e.stopPropagation();
                }
            }
        }

        function getAdultTitles() : () => string {
            return resourceService.getDeferred('ADULTTITLES');
        }

        function detailsMatch(booking: Booking) : boolean {
            const guest = _.get<Person>(booking, 'UserData.Rooms[0].Guests[0]');
            const contact = booking.UserData.Contact;
            return (!contact.FirstName && !contact.LastName && !contact.Title)
                ? true
                : guest.FirstName === contact.FirstName &&
                    guest.LastName === contact.LastName &&
                    _getTitleIndex(contact.Title) === getAdultTitles()().split(',').indexOf(guest.Title);
        }

        function clearContact(userData : UserData) : void {
            (<HTMLInputElement>document.querySelector
                ('.contact-section.page-content-panel [name="contactForm"] [name="LoyaltyNumber"]'))
                .removeAttribute('disabled');
            (<HTMLInputElement>document.querySelectorAll
                ('[name="passengers"] .guest-panel [name="loyaltyNumber"]')[0])
                .removeAttribute('disabled');
            (<HTMLInputElement>document.querySelectorAll
                ('[name="passengers"] .guest-panel [name="loyaltyNumber"]')[0]).value = '';
            userData.Contact = <Contact>{
                CountryCode: TRPCONFIG.Domain.CountryCode,
                Title: '',
                FirstName: '',
                LastName: '',
                MobileTel : '',
                HomeTel : '',

            };
        }

        function clearGuests(rooms: Room[]) : void {
            _.forEach(rooms, room => {
                _.forEach(room.Guests, g => {
                    g.Title = '';
                    g.FirstName = '';
                    g.LastName = '';
                    g.DateOfBirth = '';
                    g.LoyaltyNumber = '';
                });
            });
        }

        function updateDeposit(deposit: ng.INgModelController,
                               payment : PaymentDetails,
                               totalCost,
                               minDeposit) {
            $timeout(function () {
                if (deposit && deposit.$valid) {
                    payment.PaymentAmount = parseFloat(deposit.$modelValue);
                    return;
                }
                const errors = Object.keys(deposit.$error);
                if (errors.length === 1) {
                    if (deposit.$error.maxValue) {
                        payment.PaymentAmount = parseFloat(totalCost);
                        return;
                    }
                }
                payment.PaymentAmount = parseFloat(minDeposit);
            });
        }

        function getDiscountForCard(bookingId: string, cardNumber: string, domainId: number, cultureCode: string) {
            return $http.get(`${url}getdiscountforcard`, {
                params: {
                    'basketId': bookingId,
                    'cardNumber': cardNumber,
                    'domainId': domainId,
                    'cultureCode': cultureCode
                }
            }
            ).then(result => {
                return result.data.DiscountDetails.Discount;
            });
        }

        function getCardDiscountsForDomain(domainId: number, cultureCode: string) {
            return $http.get(`${url}getcarddiscountsfordomain`, {
                    params: {
                        'domainId': domainId,
                        'cultureCode': cultureCode
                    },
                    cache: true
                }
            ).then(result => {
                return result.data;
            });
        }

        function pauseRecording(agentIpAddress) {
            return $http.post(`${gmtUrl}pauserecording`, {
                params: {
                    'AgentIpAddress': agentIpAddress
                }
            });
        }

        function resumeRecording(agentIpAddress) {
            return $http.post(`${gmtUrl}resumerecording`, {
                params: {
                    'AgentIpAddress': agentIpAddress
                }
            });
        }

        return service;
    }
}
